import type { SessionJig, SessionLine } from '@features/workshop-plan/types';

import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Zap, Lock, Truck, TriangleAlert } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Skeleton } from '@shared/components/ui/skeleton';
import { getApiErrorMessage } from '@shared/utils/api-error';
import { StatusLabel } from '@shared/components/status-label';
import { Alert, AlertTitle } from '@shared/components/ui/alert';
import { MediaThumbnails } from '@shared/components/media-preview';
import { QuantityStepper } from '@shared/components/quantity-stepper';
import { RadioGroup, RadioGroupCard } from '@shared/components/ui/radio-group';
import {
  Dialog,
  DialogBody,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogContent,
  DialogDescription,
} from '@shared/components/ui/dialog';

import { getStatusStep } from '@features/customization-session';
import { pickJig, sortJigs } from '@features/workshop-plan/utils/session-jig';
import { useCreateSessionMutation, useGetSessionOptionsQuery } from '@features/workshop-plan/api/workshop-plan-api';

type Props = {
  familyId: number;
  familyName: string;
  /** References of the orders of the family not at the workshop yet. */
  incomingReferences: string[];
  onClose: () => void;
};

/** Orders on their way: shown to prepare what comes next, they cannot be part of the session yet. */
const IncomingOrders = ({ references }: { references: string[] }) => (
  <section aria-labelledby="incoming-orders-title" className="mt-6 flex flex-col gap-2 border-t border-border pt-6">
    <div className="flex items-center gap-2">
      <Truck aria-hidden className="size-5 shrink-0 text-muted-foreground" />
      <h3 className="text-base font-semibold" id="incoming-orders-title">
        Commandes à venir
      </h3>
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground tabular-nums">
        {references.length}
      </span>
    </div>
    <p className="text-sm text-muted-foreground">
      Pas encore arrivées à l&apos;atelier : elles ne peuvent pas encore entrer dans une session.
    </p>
    <ul className="flex flex-wrap gap-2">
      {references.map((reference) => (
        <li
          className="rounded-md border border-border bg-muted px-2.5 py-1 font-mono text-sm text-foreground tabular-nums"
          key={reference}
        >
          {reference}
        </li>
      ))}
    </ul>
  </section>
);

const JigOption = ({ jig }: { jig: SessionJig }) => {
  const busy = jig.ongoingSessions.length > 0;

  return (
    <RadioGroupCard
      // A busy machine stays available but is not offered first: it fades until it is chosen or hovered.
      // A blocked one (one session at a time, engraving) cannot be chosen at all.
      className={cn(
        'flex-col items-stretch gap-2 rounded-xl border p-4 data-[state=checked]:border-primary data-[state=unchecked]:hover:opacity-100',
        busy && !jig.blocked && 'opacity-65 data-[state=checked]:opacity-100',
        jig.blocked && 'hover:bg-transparent data-[state=unchecked]:hover:opacity-50'
      )}
      disabled={jig.blocked}
      value={String(jig.id)}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span aria-hidden className="size-3 shrink-0 rounded-full" style={{ background: jig.machineColor }} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-base font-semibold">{jig.machineName}</span>
          <span className="text-sm text-muted-foreground">
            {`Gabarit ${jig.name} · ${jig.capacity} produit${jig.capacity > 1 ? 's' : ''} max`}
          </span>
        </span>
      </span>

      {jig.blocked && (
        <span className="flex flex-col rounded-lg border border-destructive/50 px-3 py-1.5 text-xs text-destructive">
          <span className="flex items-center gap-1.5 font-semibold">
            <Lock aria-hidden className="size-4" />
            Machine occupée
          </span>
          <span>Une seule session à la fois sur cette machine : terminez la session en cours avant d&apos;en créer une.</span>
        </span>
      )}

      {busy && (
        <span
          className={cn(
            'flex flex-col rounded-lg border px-3 py-1.5 text-xs',
            jig.blocked ? 'border-border text-muted-foreground' : 'border-warning/50 text-warning'
          )}
        >
          <span className="flex items-center gap-1.5 font-semibold">
            <TriangleAlert aria-hidden className="size-4" />
            {jig.ongoingSessions.length > 1 ? `${jig.ongoingSessions.length} sessions en cours` : 'Session en cours'}
          </span>
          {jig.ongoingSessions.map((ongoing) => (
            <span className="[overflow-wrap:anywhere]" key={ongoing.id}>
              {ongoing.familyName} · {ongoing.quantity} produit{ongoing.quantity > 1 ? 's' : ''} ·{' '}
              {getStatusStep(ongoing.status).label}
            </span>
          ))}
        </span>
      )}
    </RadioGroupCard>
  );
};

const LineOption = ({
  line,
  quantity,
  disabled,
  onToggle,
}: {
  line: SessionLine;
  /** Quantity taken by the session (0 = not chosen). */
  quantity: number;
  disabled: boolean;
  onToggle: () => void;
}) => {
  const unavailable = disabled && quantity === 0;

  return (
    <div
      aria-checked={quantity > 0}
      aria-disabled={unavailable}
      className={cn(
        'flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-3 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary',
        quantity > 0 ? 'border-primary' : 'border-border',
        unavailable && 'pointer-events-none opacity-50'
      )}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      role="checkbox"
      tabIndex={unavailable ? -1 : 0}
    >
      <Checkbox checked={quantity > 0} className="pointer-events-none mt-0.5" tabIndex={-1} />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{line.order}</span>
          {line.express && (
            <StatusLabel color="error" startIcon={<Zap />} variant="soft">
              Express
            </StatusLabel>
          )}
          <span className="text-xs text-muted-foreground">
            {quantity > 0 ? `${quantity} / ${line.quantity}` : `${line.quantity} à personnaliser`}
          </span>
        </div>
        <span className="truncate text-sm">{line.product}</span>
        <MediaThumbnails medias={line.medias} title={`${line.order} · ${line.product}`} />
      </div>
    </div>
  );
};

/**
 * Starts a customization session for a family: the machine (through its jig), then the quantity.
 * A complex family shows the products with their visuals so the operator picks the ones of the session.
 */
export const CustomizationSessionDialog = ({ familyId, familyName, incomingReferences, onClose }: Props) => {
  const { data: options, isLoading, isError } = useGetSessionOptionsQuery(familyId);
  const navigate = useNavigate();
  const [createSession, { isLoading: isCreating }] = useCreateSessionMutation();

  const [jigId, setJigId] = useState<number | null>(null);
  const [typedQuantity, setTypedQuantity] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<number, number>>({});

  const jig = pickJig(options?.jigs ?? [], jigId);
  const capacity = jig?.capacity ?? 0;
  const maximum = Math.min(capacity, options?.available ?? 0);

  const chosenTotal = useMemo(() => Object.values(chosen).reduce((sum, quantity) => sum + quantity, 0), [chosen]);

  const quantityValue = typedQuantity ?? String(maximum);
  const quantity = Number(quantityValue);
  const quantityValid = Number.isInteger(quantity) && quantity >= 1 && quantity <= maximum;

  const complex = options?.complex ?? false;
  const canSubmit = jig !== null && !isCreating && (complex ? chosenTotal > 0 : quantityValid);

  const selectJig = (next: SessionJig) => {
    setJigId(next.id);
    setTypedQuantity(null);
    setChosen({});
  };

  const toggleLine = (line: SessionLine) => {
    setChosen((previous) => {
      if (previous[line.groupId]) {
        return Object.fromEntries(Object.entries(previous).filter(([groupId]) => Number(groupId) !== line.groupId));
      }
      const total = Object.values(previous).reduce((sum, value) => sum + value, 0);
      const taken = Math.min(line.quantity, capacity - total);
      return taken > 0 ? { ...previous, [line.groupId]: taken } : previous;
    });
  };

  const submit = async () => {
    if (!jig) return;

    try {
      const created = await createSession({
        familyId,
        jigId: jig.id,
        ...(complex ? { groups: chosen } : { quantity }),
      }).unwrap();
      toast.success(
        `Session créée sur ${created.machine} : ${created.quantity} produit${created.quantity > 1 ? 's' : ''}`
      );
      onClose();
      navigate(`/workshop/sessions/${created.id}`);
    } catch (exception) {
      toast.error(getApiErrorMessage(exception));
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open>
      <DialogContent className="max-w-300" showCloseButton={false}>
        <DialogHeader className="border-b border-border">
          <DialogTitle>Session de personnalisation · {familyName}</DialogTitle>
          <DialogDescription className="sr-only">Créer une session de personnalisation</DialogDescription>
        </DialogHeader>
        <DialogBody className="py-6">
          {isLoading && <Skeleton className="h-50 rounded-lg" />}

          {isError && (
            <Alert variant="destructive">
              <AlertTitle className="line-clamp-none">
                Les options de la session n&apos;ont pas pu être chargées.
              </AlertTitle>
            </Alert>
          )}

          {options && options.jigs.length === 0 && (
            <Alert variant="warning">
              <AlertTitle className="line-clamp-none">
                Aucun gabarit sur une machine active n&apos;est défini pour cette famille.
              </AlertTitle>
            </Alert>
          )}

          {options && options.available === 0 && options.jigs.length > 0 && (
            <Alert className="border-info/50 text-info">
              <AlertTitle className="line-clamp-none">
                Aucun produit de cette famille n&apos;est disponible à l&apos;atelier pour le moment.
              </AlertTitle>
            </Alert>
          )}

          {options && options.available > 0 && options.jigs.length > 0 && jig === null && (
            <Alert variant="warning">
              <AlertTitle className="line-clamp-none">
                Toutes les machines de cette famille sont occupées : une seule session à la fois sur chacune.
              </AlertTitle>
            </Alert>
          )}

          {options && options.jigs.length > 0 && options.available > 0 && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h3 className="text-base font-semibold">Machine</h3>
                <RadioGroup
                  className="grid-cols-[repeat(auto-fill,minmax(320px,1fr))] items-start gap-2"
                  onValueChange={(value) => {
                    const next = options.jigs.find((candidate) => String(candidate.id) === value);
                    if (next) selectJig(next);
                  }}
                  value={jig ? String(jig.id) : ''}
                >
                  {sortJigs(options.jigs).map((candidate) => (
                    <JigOption jig={candidate} key={candidate.id} />
                  ))}
                </RadioGroup>
              </div>

              {complex ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">Produits de la session</h3>
                    <span className="text-sm text-muted-foreground">
                      {chosenTotal} / {capacity}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Vérifiez le rendu final de chaque visuel avant de le choisir. Les commandes express passent en
                    premier.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {options.lines.map((line) => (
                      <LineOption
                        disabled={chosenTotal >= capacity}
                        key={line.groupId}
                        line={line}
                        onToggle={() => toggleLine(line)}
                        quantity={chosen[line.groupId] ?? 0}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <h3 className="text-base font-semibold">Quantité à personnaliser</h3>
                  <QuantityStepper
                    error={!quantityValid}
                    helperText={`Entre 1 et ${maximum} (${options.available} disponible${options.available > 1 ? 's' : ''}, capacité du gabarit : ${capacity}). Les commandes express passent en premier.`}
                    label="Quantité"
                    onChange={setTypedQuantity}
                    value={quantityValue}
                  />
                </div>
              )}
            </div>
          )}

          {!isLoading && incomingReferences.length > 0 && <IncomingOrders references={incomingReferences} />}
        </DialogBody>
        <DialogFooter className="flex-row items-center border-t border-border">
          {options && complex && (
            <span className="mr-auto text-sm text-muted-foreground tabular-nums">
              {chosenTotal} / {capacity} produits dans la session
            </span>
          )}
          <Button className="ml-auto" onClick={onClose} variant="outline">
            Annuler
          </Button>
          <Button disabled={!canSubmit} loading={isCreating} onClick={submit}>
            Créer la session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
